import { DialogClose } from '@radix-ui/react-dialog';

import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter } from './ui/dialog';

interface TermsAndConditionsDialogProps {
  open: () => boolean;
  onOpenChange: (open: boolean) => void;
}

export function TermsAndConditionsDialog({ open, onOpenChange }: TermsAndConditionsDialogProps) {
  return (
    <Dialog onOpenChange={onOpenChange} open={open()}>
      <DialogContent className="overflow-y-scroll p-8 sm:max-w-[700px]">
        <h2 className="mb-4 ml-10 text-xl font-bold">ONLINE BIDDING TERMS AND CONDITIONS</h2>
        <p className="ml-10">
          These online bidding terms and conditions (“the Online Bidding Conditions”) apply to online and dual bidding
          platform at www.best2bid.com (hereinafter referred to as “BEST2BID”). By registering to bid online, the bidder
          expressly agree to these Online Bidding Conditions. These Online Bidding Conditions are supplemental, meaning
          that they shall apply in addition to the{' '}
          <span className="font-bold">
            Standard Conditions of Sale that can be found at the event information of BEST2BID
          </span>{' '}
          (“the Standard Conditions”) and other terms and conditions applicable to the Bidder and the Buyer
          participating in the subject auction and shall be deemed incorporated by reference and made a part of the
          Conditions. The Conditions are published in the Catalogue for the auction and/or on this portal at each and
          every auction event, among other places. Additional information applicable to the auction may also be set out
          in the Catalogue, on this portal, and/or in a notice displayed at the auction venue, and it is the Bidder’s
          responsibility to review that information. Please be aware that announcements affecting the auction may also
          be made orally before and during the auction without prior notice. In registering to bid at any auction, the
          Bidder agrees to the applicable Standard Conditions and the Online Bidding Conditions.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">1.</span> Definitions
        </h3>
        <div className="ml-10">
          <p>In the Online Bidding Conditions:</p>
          <p>
            “Online Bidding” is a service in which auction users or participants sell or bid for products or services
            via the Internet.
          </p>
          <p>
            “Online Bidding Account” is an individual online bidding’s user interface with eligibility to bid at an
            auction via internet, subject to BEST2BID accepting and validating an online bidding registration submitted
            by the intended Bidder.
          </p>
          <p>
            “Authorized Vendor” is a registered vendor permitted by BEST2BID to undertake conduct of online bidding
            activity including registration of bidder, setting its Standard Conditions and manage cum administer its
            product(s) to be auctioned
          </p>
        </div>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">2.</span> Registration
        </h3>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">2.1</span> Before the Bidder can bid via Online Bidding, the Bidder must
          register at BEST2BID to bid online. Please note that the Bidder must be registered at least 24 hours prior to
          the start of any auction or the Bidder may not be able to bid online in that auction.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">2.2</span> Once the Bidder has registered, the Bidder should keep his or her
          online account details strictly confidential and the Bidder must not permit any third party to use or access
          his or her account on behalf or otherwise. The Bidder will be liable for any and all bids made via his or her
          account.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">2.3</span> The Bidder will need to register for each auction event that he or
          she wish to bid on. This can be done through the relevant auction details screen.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">3.</span> Online Bidding Process
        </h3>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">3.1</span> Once the Bidder has successfully registered for Online Bidding in
          an auction, he or she will be eligible to bid online at that auction event.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">3.2</span> During the auction, the Bidder can bid for any Lot (or any specific
          lot as permitted under the auction event’s Standard Conditions) by clicking the bid button. Each bid shall be
          final and binding as soon as the Bidder click the bid button.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">3.3</span> The current bid will be displayed on the Bidder’s screen during the
          Sale. In the event of a tie between a bid placed by the Bidder online and any other identical bid(s)
          (including bids made in person at the auction, telephone and absentee bids), it will be at the auctioneer's
          discretion as to which bid shall be accepted.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">3.4</span> The Bidder acknowledge that he or she is bidding in a live auction
          and the Bidder agrees that each bid submitted as provided in the Online Bidding Conditions is irrevocable and
          cannot be amended or corrected, even if submitted in error and notified to us. The Bidder accept full
          liability for all bids submitted via his or her Online Bidding Account (including the liability to pay in full
          and on time in accordance with the Conditions for any lot that is the subject of a successful bid submitted
          from his or her account).
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">3.5</span> Please note that BEST2BID and/or its Authorized Vendor reserves the
          right to reject any registration to bid online, withdraw its permission for the Bidder to use online bidding,
          or terminate an Online Bidding account, for any reason at any time prior to, during or after a Sale.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">4.</span> After the Sale
        </h3>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">4.1</span> To find out if the Bidder has been successful in the bidding, he or
          she may check the results that are published by BEST2BID and/or its Authorized Vendor online after the
          auction. An email will be send to the Bidder upon he or she winning any bid. The Bidder may contact the
          Authorized Vendor if necessary.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">4.2</span> The Bidder agrees to pay for and collect any lot that is the
          subject of a successful bid submitted by the Bidder or from his or her Online Bidding account, in accordance
          with the Conditions.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">4.3</span> Upon successful bidding, a notification email will be sent to the
          Bidder for each successful Lot. The Bidder will be notified with Invoice and payment procedure upon end of
          auction.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">5.</span> Use of the Online Bidding Service and Limitations of BEST2BID's Liability
        </h3>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.3</span> If the Bidder chooses to use the Online Bidding service, the Bidder
          do so entirely at his or her own risk. Access to and use of Online Bidding is dependent upon, among other
          things, the availability of the internet and the speed and quality of internet connections, and BEST2BID
          therefore encourage bidders to attend Sales in person whenever possible.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.4</span> A currency conversion tool may be provided on the Online Bidding
          screen to enable estimates for lots and the final purchase price to be displayed in certain currencies. The
          currency conversion tool is provided for the Bidder’s convenience only and is an approximate guide to the
          equivalent amount in the specified currencies. The Bidder is therefore advised to conduct his or her own
          research into the applicable exchange rates prior to placing a bid. Please note that when the auction is
          conducted, all bids must be made in the currency of the country in which the auction is held. All bidding
          increments will be in that currency only. Payment for any lot that is the subject of a successful bid must
          also be made in the currency of the auction and not in any other currency (even if that currency is displayed
          on the currency conversion tool). BEST2BID do not accept any responsibility for any errors that may occur as a
          consequence of reliance upon the currency conversion tool.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.5</span> The content displayed via the Online Bidding service may contain
          inaccuracies and typographical errors and BEST2BID do not warrant the accuracy or completeness of the content
          or that any defects will be corrected. Any reliance on any such content, advice, statement, or other
          information shall be at the Bidder’s sole risk. BES2BID reserves the right, in our sole discretion, to correct
          any errors or omissions in any portion of the Online Bidding service and to make any changes to the features,
          functionality or content of the Online Bidding service at any time.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.6</span> Access to the Online Bidding service is made available as a
          convenience and on a temporary basis and BEST2BID reserves the right to suspend or terminate any aspect or
          feature of the Online Bidding service (including the Online Bidding service in its entirety) at any time, with
          or without notice. Without limiting the foregoing, BEST2BID may suspend access to Online Bidding to carry out
          scheduled or unscheduled maintenance or for any other reason at any time.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.7</span> BEST2BID accept no liability for any failure or delay in executing
          bids or any errors contained in bids placed via the online bidding service.
        </p>
        <p className="flex flex-row gap-4">
          <span className="font-semibold">5.8</span> BEST2BID and its suppliers provide the online bidding service on
          "as is where is" basis and without any warranty or condition, express, implied or statutory. Without limiting
          the foregoing, BEST2BID accept no liability for any failures, delays or errors caused by interruptions in the
          availability of the online bidding service or our portal or any errors or defects in their content or
          functionality, any software and/or hardware defects and/or any internet connection problems, and BEST2BID do
          not represent or warrant that the online bidding service or the portal will be error-free, free of viruses or
          other harmful components, or that any defects will be corrected.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">6.</span> Changes to The Conditions and the Online Bidding Conditions
        </h3>
        <p className="ml-10">
          BEST2BID may from time to time make changes to the Standards Conditions and the Online Bidding Conditions. Any
          changes will be posted on our website at www.best2bid.com and we will endeavor to notify the Bidder of the
          same by means of a notice and link to the revised terms on our portal. Please review these terms and
          conditions regularly to ensure the Bidder is aware of any changes made by us. If the Bidder uses Online
          Bidding after changes have been made and posted, he or she shall be deemed to have agreed to such changes. If
          the Bidder do not agree to such changes, he or she should not make any subsequent use of the Online Bidding
          service.
        </p>
        <h3 className="mt-4 text-lg font-semibold">
          <span className="mr-5">7.</span> Governing Law
        </h3>
        <p className="ml-10">
          The BEST2BID Online Bidding Terms and Conditions shall be governed by, and construed in accordance with the
          laws of Malaysia and the parties hereto subject to the jurisdiction of the courts of Malaysia and waive any
          objection on the ground of venue or forum non-convenient or other grounds.
        </p>

        <div className="sticky bottom-0 max-h-full bg-white">
          <Button onClick={() => onOpenChange(false)} className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
